import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Gradient glass-style action menu (pill options) for bulk selection toolbars.
 * options: { value, label, iconClass? }
 */
const GlassBulkActionSelect = ({
  options = [],
  placeholder = "Select action",
  disabled = false,
  onChange,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const handleSelect = (value) => {
    close();
    onChange?.({ value });
  };

  const normalizedOptions = (Array.isArray(options) ? options : []).filter(
    (opt) => String(opt?.value ?? "").trim() && String(opt?.label ?? "").trim()
  );

  return (
    <div
      ref={rootRef}
      className={["glass-bulk-action-select", className].filter(Boolean).join(" ")}
    >
      <button
        type="button"
        className={[
          "glass-bulk-action-select__trigger",
          open ? "glass-bulk-action-select__trigger--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span className="glass-bulk-action-select__trigger-icon" aria-hidden>
          <i className="mdi mdi-robot" />
        </span>
        <span className="glass-bulk-action-select__trigger-label">{placeholder}</span>
        <span
          className={[
            "glass-bulk-action-select__trigger-chevron",
            open ? "glass-bulk-action-select__trigger-chevron--open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        >
          <i className="mdi mdi-chevron-down" />
        </span>
      </button>

      {open ? (
        <div className="glass-bulk-action-select__menu" role="listbox">
          <div className="glass-bulk-action-select__menu-inner">
            {normalizedOptions.map((opt) => {
              const iconClass = String(opt?.iconClass ?? "mdi mdi-circle-small").trim();
              const isDanger = Boolean(opt?.tone === "danger");
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  className={[
                    "glass-bulk-action-select__option",
                    isDanger ? "glass-bulk-action-select__option--danger" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(opt.value)}
                >
                  <span className="glass-bulk-action-select__option-icon" aria-hidden>
                    <i className={iconClass} />
                  </span>
                  <span className="glass-bulk-action-select__option-label">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GlassBulkActionSelect;
